import { api } from "./client";

/** Mensagens ativas and template button actions (decision 0036): the shapes the API answers. */
export type ActiveStatus = "can_receive" | "paused" | "opted_out";
export const PAUSE_PERIODS = ["7d", "30d", "3m", "6m", "1y"] as const;
export type PausePeriod = (typeof PAUSE_PERIODS)[number];

export interface ActiveHistoryItem {
  status: ActiveStatus;
  until: string | null;
  at: string;
  source: string;
  evidence: Record<string, unknown> | null;
}
export interface ActiveMessages {
  status: ActiveStatus;
  /** The day the company may write again ("2027-01-24"), while paused. */
  until: string | null;
  since: string | null;
  source: string | null;
  evidence: Record<string, unknown> | null;
  history: ActiveHistoryItem[];
  /** What a utility template does while paused or opted out (GET only). */
  utility?: "send" | "hold" | "ask";
}
export interface ActiveSettings {
  utility: "send" | "hold" | "ask";
  onPauseEnd: {
    tagId: string | null;
    flowId: string | null;
    teamId: string | null;
  };
  assistant: boolean;
}

export type MainAction =
  | { kind: "pause"; period: PausePeriod }
  | { kind: "opt_out" }
  | { kind: "opt_in" }
  | { kind: "route"; to: "team" | "flow" | "assistant"; id: string | null }
  | { kind: "close"; outcome: string }
  | { kind: "record" };
export type AlsoAction =
  | { kind: "tag_add"; tagId: string }
  | { kind: "tag_remove"; tagId: string }
  | { kind: "field"; key: string; value: string };
export interface ButtonRule {
  index: number;
  text: string;
  main: MainAction;
  also: AlsoAction[];
}

const base = (slug: string) => `/v1/c/${slug}`;

export const activeMessagesApi = {
  get: (slug: string, contactId: string) =>
    api<ActiveMessages>(`${base(slug)}/contacts/${contactId}/active-messages`),
  set: (
    slug: string,
    contactId: string,
    body: {
      status: ActiveStatus;
      until?: string;
      period?: PausePeriod;
      note?: string;
    },
  ) =>
    api<ActiveMessages>(`${base(slug)}/contacts/${contactId}/active-messages`, {
      method: "POST",
      body,
    }),
  settings: (slug: string) =>
    api<ActiveSettings>(`${base(slug)}/settings/active-messages`),
  saveSettings: (slug: string, body: ActiveSettings) =>
    api<ActiveSettings>(`${base(slug)}/settings/active-messages`, {
      method: "PUT",
      body,
    }),
  saveButtonActions: (
    slug: string,
    templateId: string,
    buttons: ButtonRule[],
  ) =>
    api<{ template: { id: string; button_actions: ButtonRule[] } }>(
      `${base(slug)}/templates/${templateId}/button-actions`,
      { method: "PUT", body: { buttons } },
    ),
};

/** The action a button's words suggest, as the server's suggestAction (B1). */
export function suggestAction(text: string): MainAction {
  const t = text
    .normalize("NFD")
    .replace(/\p{Mn}/gu, "")
    .trim()
    .toLowerCase();
  if (
    /^(agora nao|depois|mais tarde|outro dia|ahora no|despues|later|not now)\b/.test(
      t,
    )
  )
    return { kind: "pause", period: "3m" };
  if (
    /(sair|parar|stop|nao (tenho )?interesse|nao quero|descadastr|cancelar inscricao|no me interesa|unsubscribe|not interested)/.test(
      t,
    )
  )
    return { kind: "opt_out" };
  return { kind: "route", to: "team", id: null };
}

/** "24/01/2027" from "2027-01-24", in the reader's language. */
export function dayLabel(date: string, locale: string): string {
  const [y, m, d] = date.split("-").map(Number) as [number, number, number];
  return new Intl.DateTimeFormat(locale, {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    timeZone: "UTC",
  }).format(new Date(Date.UTC(y, m - 1, d)));
}
